import { useEffect, useState, useRef } from "react";
import { Form } from "react-bootstrap";

const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  isLoading,
  error,
  idField = "id",
  nameField = "name",
  displayFormat = (option) => `${option[nameField]} (${option[idField]})`,
  isClearable = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Reset searchTerm to the selected option's nameField or clear it
        if (value) {
          const selectedOption = options?.find(
            (option) => String(option[idField]) === String(value)
          );
          setSearchTerm(selectedOption ? selectedOption[nameField] : "");
        } else {
          setSearchTerm("");
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [options, value, idField, nameField]);

  // Sync searchTerm with value prop when value or options change
  useEffect(() => {
    if (value) {
      const selectedOption = options?.find(
        (option) => String(option[idField]) === String(value)
      );
      setSearchTerm(selectedOption ? selectedOption[nameField] : "");
    } else {
      setSearchTerm("");
    }
  }, [value, options, idField, nameField]);

  const filteredOptions = options?.filter(
    (option) =>
      String(option[nameField] || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      String(option[idField] || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleSelect = (option) => {
    setSearchTerm(option[nameField]);
    onChange(option[idField]);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSearchTerm("");
    onChange("");
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="position-relative">
      <div className="position-relative">
        <Form.Control
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          className={error ? "form-control is-invalid" : "form-control"}
        />
        {isClearable && searchTerm && (
          <button
            type="button"
            className="position-absolute top-50 end-0 translate-middle-y bg-transparent border-0 p-2"
            onClick={handleClear}
            style={{ zIndex: 10 }}
            aria-label="Clear selection"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
        )}
      </div>
      {isOpen && (
        <div
          className="position-absolute w-100 bg-white border rounded mt-1"
          style={{
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 1000,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {isLoading ? (
            <div className="p-2 text-muted">Loading...</div>
          ) : filteredOptions?.length > 0 ? (
            (() => {
              // Find the index of the first matching option to avoid highlighting duplicates
              const firstMatchIndex = filteredOptions.findIndex(
                (opt) => String(opt[idField]) === String(value)
              );
              return filteredOptions.map((option, idx) => {
                const isSelected = idx === firstMatchIndex && value !== "" && String(value) === String(option[idField]);
                return (
                  <div
                    key={`${option[idField]}-${idx}`}
                    className="p-2 hover-bg-light cursor-pointer"
                    style={{
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#e9ecef" : "transparent",
                    }}
                    onClick={() => handleSelect(option)}
                  >
                    {displayFormat(option)}
                  </div>
                );
              });
            })()
          ) : (
            <div className="p-2 text-muted">No results found</div>
          )}
        </div>
      )}
      {error && (
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
      )}
      <style jsx>{`
        .hover-bg-light:hover {
          background-color: #f8f9fa;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .top-50 {
          top: 50%;
        }
        .end-0 {
          right: 0;
        }
        .translate-middle-y {
          transform: translateY(-50%);
        }
      `}</style>
    </div>
  );
};

export default CustomDropdown;
